import React, { useEffect, useState } from 'react';
import {
  AuthClient,
  UserClient,
  UserCreateDTO,
  UserDTO,
  UserEditDTO,
} from '../../services/apiClient';
import FormField from '../../components/common/FormField';
import Button from '../../components/common/Button';
import PasswordRequirements from '../common/PasswordRequirements';
import FileUpload from '../common/FileUpload';
import Modal from '../../components/common/Modal';
import TwoFactorAuthSetup from './TwoFactorAuthSetup';
import { API_BASE_URL } from '../../config/config';
import axiosInstance from '../../services/axiosInstance';
import { X } from 'lucide-react';
import { useRoles } from '../../hooks/useRoles';

interface UserFormProps {
  initialValues: UserCreateDTO | UserEditDTO;
  onSave: (user: UserCreateDTO | UserEditDTO) => void;
  onCancel: () => void;
  setProfilePicture?: (file: File | null) => void;
  isCreating: boolean;
}

const UserForm: React.FC<UserFormProps> = ({
  initialValues,
  onSave,
  onCancel,
  setProfilePicture,
  isCreating,
}) => {
  const [formData, setFormData] = useState(initialValues);
  const [profilePictureUrl, setProfilePictureUrl] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showTwoFactorModal, setShowTwoFactorModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [twoFactorSetupComplete, setTwoFactorSetupComplete] = useState(false); // Tracks if setup is complete
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const authClient = new AuthClient(API_BASE_URL, axiosInstance);
  const userClient = new UserClient(API_BASE_URL, axiosInstance);

  const {roles} = useRoles();

  // Password validation states
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    digit: false,
  });

  useEffect(() => {
    if (
      !isCreating &&
      typeof (initialValues as UserEditDTO).twoFactorEnabled === 'boolean'
    ) {
      setTwoFactorEnabled(
        (initialValues as UserEditDTO).twoFactorEnabled ?? false,
      );
    }

    const fetchProfilePicture = async () => {
      if (!isCreating && initialValues) {
        try {
          const profilePicResponse = await userClient.generateProfilePictureSas(
            (initialValues as UserEditDTO).id ?? '',
          );
          if (profilePicResponse.success) {
            setProfilePictureUrl(profilePicResponse.data ?? '');
          }
        } catch (error) {
          console.error('Error fetching profile picture:', error);
        }
      }
    };

    fetchProfilePicture(); // Call the async function inside useEffect
  }, [isCreating, initialValues]);

  const validatePassword = (password: string) => {
    setPasswordValidation({
      length: password.length >= 6,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      digit: /\d/.test(password),
    });
  };

  const validateForm = () => {
    const formErrors: { [key: string]: string } = {};

    if (!formData.firstName?.trim())
      formErrors.firstName = 'First name is required.';
    if (!formData.lastName?.trim())
      formErrors.lastName = 'Last name is required.';

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      formErrors.email = 'Valid email is required.';
    }

    if(!formData.roleId)
      formErrors.roleId = 'Select a user role.'

    if (isCreating) {
      // Password validation
      if (!password.trim()) {
        formErrors.password = 'Password is required.';
      } else {
        if (!passwordValidation.length) {
          formErrors.password = 'Password must be at least 6 characters.';
        }
        if (!passwordValidation.uppercase) {
          formErrors.password =
            'Password must contain at least one uppercase letter.';
        }
        if (!passwordValidation.lowercase) {
          formErrors.password =
            'Password must contain at least one lowercase letter.';
        }
        if (!passwordValidation.digit) {
          formErrors.password = 'Password must contain at least one digit.';
        }
      }

      if (password !== confirmPassword) {
        formErrors.confirmPassword = 'Passwords do not match.';
      }
    }

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const handleInputChange =
    (key: keyof UserCreateDTO | keyof UserEditDTO) => (value: string) => {
      setFormData({
        ...formData,
        [key]: value,
      });
    };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (file && setProfilePicture) setProfilePicture(file);
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    validatePassword(value);
  };

  const handleTwoFactorChange = async (enabled: boolean) => {
    setTwoFactorEnabled(enabled);
    if (enabled) {
      try {
        const response = await authClient.setupTwoFactor(
          (formData as UserEditDTO).id ?? '',
        );
        setQrCodeUrl(response.data?.qrCodeUrl ?? '');
        setShowTwoFactorModal(true);
      } catch (error) {
        console.error('Failed to setup two-factor authentication', error);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    const userPayload: UserCreateDTO | UserEditDTO = {
      ...formData,
      ...(isCreating && { password }), // Include password only if creating
      twoFactorEnabled,
    };

    onSave(userPayload);
  };

  const closeTwoFactorModal = () => {
    setShowTwoFactorModal(false);
    if (!twoFactorSetupComplete) {
      setTwoFactorEnabled(false); // Reset only if setup is not complete
    }
  };
  const handleTwoFactorVerification = async (
    code: string,
  ): Promise<boolean> => {
    try {
      const response = await authClient.verifyTwoFactor({
        userId: (formData as UserEditDTO).id,
        code,
      });

      if (response?.success) {
        setTwoFactorSetupComplete(true); // Mark setup as complete
        return true; // Verification successful
      }

      return false; // Verification failed
    } catch (error) {
      console.error('Failed to verify two-factor code', error);
      return false; // Treat error as failure
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mb-4">
      {/* Profile Picture Section */}
      {formData.profilePicture && profilePictureUrl && (
        <div className="relative flex items-center mt-4 mb-4">
          {/* Profile Picture Wrapper */}
          <div className="relative">
            {/* Profile Picture */}
            <img
              src={profilePictureUrl}
              alt="Profile"
              style={{ width: '80px', height: '80px', objectFit: 'cover' }}
              className="rounded-full"
            />

            {/* Remove Button (Red Cross) */}
            <button
              onClick={() => setFormData({ ...formData, profilePicture: '' })}
              className="absolute top-0 right-0 transform translate-x-1/2 -translate-y-1/2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition"
              aria-label="Remove Profile Picture"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <div>
        <FileUpload
          id="profilePicture"
          placeholderText="Upload or drag and drop profile picture"
          onChange={handleFileChange}
        />
      </div>

      {/* Information Section */}
      <h2 className="text-lg font-bold text-gray-700 dark:text-gray-300 mb-4">
        Information
      </h2>
      <hr />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 mb-4">
        <FormField
          id="firstName"
          label="First Name"
          value={formData.firstName || ''}
          onChange={(e) => handleInputChange('firstName')(e.target.value)}
          placeholder="Enter first name"
          error={errors.firstName}
        />
        <FormField
          id="lastName"
          label="Last Name"
          value={formData.lastName || ''}
          onChange={(e) => handleInputChange('lastName')(e.target.value)}
          placeholder="Enter last name"
          error={errors.lastName}
        />
        <FormField
          id="jobTitle"
          label="Job Title"
          value={formData.jobTitle || ''}
          onChange={(e) => handleInputChange('jobTitle')(e.target.value)}
          placeholder="Enter job tite"
          error={errors.jobTitle}
        />
        <FormField
          id="email"
          label="Email"
          value={formData.email || ''}
          onChange={(e) => handleInputChange('email')(e.target.value)}
          placeholder="Enter email"
          error={errors.email}
        />
        <FormField
              id="roleId"
              label="Role"
              type="dropdown"
              value={formData.roleId || ''}
              onChange={(e) => handleInputChange('roleId')(e.target.value)}
              options={roles.map((role) => ({
                value: role.id,
                label: role.name || '',
              }))}
              error={errors.roleId}
            />
        {!isCreating && (
          <FormField
            id="twoFactorEnabled"
            label="Enable Two-Factor Authentication?"
            type="checkbox"
            value={twoFactorEnabled}
            onChange={(e) =>
              handleTwoFactorChange((e.target as HTMLInputElement).checked)
            }
          />
        )}
      </div>

      {/* Credentials Section */}
      {isCreating && (
        <>
          <h2 className="md font-bold text-gray-700 dark:text-gray-300 mb-4">
            Credentials
          </h2>
          <hr />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 mb-4">
            <div>
              <FormField
                id="password"
                label="Password"
                type="password"
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
                placeholder="Enter password"
                error={errors.password}
              />
              <PasswordRequirements validation={passwordValidation} />
            </div>
            <FormField
              id="confirmPassword"
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm password"
              error={errors.confirmPassword}
            />
          </div>
        </>
      )}

      <div className="flex justify-end space-x-4">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">{isCreating ? 'Create' : 'Save'}</Button>
      </div>

      {/* Modal for Two-Factor Authentication */}
      <Modal
        isOpen={showTwoFactorModal}
        onClose={closeTwoFactorModal}
        title="Two-Factor Authentication Setup"
      >
        <TwoFactorAuthSetup
          qrCodeUrl={qrCodeUrl ?? ''}
          onVerifyCode={handleTwoFactorVerification}
          onFinish={closeTwoFactorModal}
        />
      </Modal>
    </form>
  );
};

export default UserForm;
